import type { OptimizelyDatafile } from '#types/p13n'
import type { OptimizelyDecision } from '@optimizely/optimizely-sdk'
import {
  parseOptimizelyDatafile,
  getAttributeKeysUsedByFlag as resolveAttributeKeysUsedByFlag,
  hasFeatureFlagInDatafile as resolveHasFeatureFlagInDatafile,
  isDecisionFromRunningExperiment as resolveRunningExperimentDecision,
} from '#core/plugin/optimizelyFeature/utils'
import { optimizelySessionStorageCmabCache } from '#core/utils/optimizelyCmabCache'
import { optimizelyLocalStorageUserProfileService } from '#core/utils/optimizelyUserProfileService'
import {
  createBatchEventProcessor,
  createInstance,
  createOdpManager,
  createPollingProjectConfigManager,
  NOTIFICATION_TYPES,
} from '@optimizely/optimizely-sdk'

type OptimizelyClient = ReturnType<typeof createInstance>
declare module '#app' {
  interface NuxtApp {
    $optimizely: {
      client: OptimizelyClient | null
      dataReady: (userEventData: any) => void
      hasFeatureFlag: (flagKey: string) => boolean
      getAttributeKeysUsedByFlag: (flagKey: string) => string[]
      isAttributeUsedByFlag: (flagKey: string, attributeKey: string) => boolean
      isDecisionFromRunningExperiment: (decision: OptimizelyDecision | undefined) => boolean
    }
  }
}

declare module '@vue/runtime-core' {
  interface ComponentCustomProperties {
    $optimizely: {
      client: OptimizelyClient | null
      dataReady: (userEventData: any) => void
      hasFeatureFlag: (flagKey: string) => boolean
      getAttributeKeysUsedByFlag: (flagKey: string) => string[]
      isAttributeUsedByFlag: (flagKey: string, attributeKey: string) => boolean
      isDecisionFromRunningExperiment: (decision: OptimizelyDecision | undefined) => boolean
    }
  }
}

export default defineNuxtPlugin({
  name: 'optimizely',
  setup: async () => {
    const {
      configP13n: { optimizelySdkKey, enableOptimizely }
    } = useFeatureFlags()
    const route = useRoute()
    useState('optimizely-landing-path', () => route.path)

    if (!enableOptimizely || isOptimizelyFxDisabled(route.query.disableOptimizely)) return {}

    const datafileUrl = `https://cdn.optimizely.com/datafiles/${optimizelySdkKey}.json`

    let datafile: OptimizelyDatafile | null = null
    const attributesUsedByFlags = new Map<string, Set<string>>()

    const userId = useCookie('optimizelyUserId', {
      maxAge: 60 * 60 * 24 * 365
    })
    const optimizelyVisitNumber = useCookie('optimizelyVisitNumber', {
      default: () => 0,
      encode: (value) => String(value),
      decode: (value) => Number(value),
      maxAge: 60 * 60 * 24 * 90
    })

    const optimizelyActiveVisit = useCookie('optimizelyActiveVisit', {
      default: () => undefined,
      encode: (value) => String(value),
      decode: (value) => value === 'true',
    })

    const optimizelyLandingPage = useCookie('optimizelyLandingPage', {
      default: () => undefined,
      encode: (value) => String(value),
      decode: (value) => value === 'true',
    })

    if (isClient()) {
      if (!optimizelyActiveVisit.value) {
        optimizelyVisitNumber.value++
        optimizelyActiveVisit.value = true
        optimizelyLandingPage.value = true
      }
      else {
        optimizelyLandingPage.value = false
      }

      userId.value ??= uuid()
    }

    if (!isClient()) {
      return {
        provide: {
          optimizely: {
            client: null,
            dataReady: () => {},
            hasFeatureFlag: () => false,
            getAttributeKeysUsedByFlag: () => [],
            isAttributeUsedByFlag: () => false,
            isDecisionFromRunningExperiment: () => false,
          },
        },
      }
    }

    let client: OptimizelyClient | null = null
    const syncDatafile = (nextDatafile: OptimizelyDatafile | null) => {
      if (!nextDatafile) return

      datafile = nextDatafile
      attributesUsedByFlags.clear()
    }

    const syncDatafileFromClient = () => {
      const nextDatafile = parseOptimizelyDatafile(client?.getOptimizelyConfig()?.getDatafile())
      if (nextDatafile) {
        syncDatafile(nextDatafile)
        return
      }

      log.warn('[Optimizely] Failed to parse updated datafile from client config')
    }

    try {
      const projectConfigManager = createPollingProjectConfigManager({
        sdkKey: optimizelySdkKey,
        autoUpdate: true,
        updateInterval: 300000,
      })

      const eventProcessor = createBatchEventProcessor({
        batchSize: 10,
        flushInterval: 1000,
        storeTtl: 20_000
      })

      const odpManager = createOdpManager({
        segmentsCacheSize: 100,
        segmentsCacheTimeout: 600000,
      })

      client = createInstance({
        projectConfigManager,
        eventProcessor,
        odpManager,
        userProfileService: optimizelyLocalStorageUserProfileService,
        cmab: {
          cache: optimizelySessionStorageCmabCache,
        },
      })
      syncDatafile(await $fetch<OptimizelyDatafile>(datafileUrl))
      syncDatafile(parseOptimizelyDatafile(client.getOptimizelyConfig()?.getDatafile()))
      client.notificationCenter.addNotificationListener(
        NOTIFICATION_TYPES.OPTIMIZELY_CONFIG_UPDATE,
        syncDatafileFromClient
      )
    }
    catch (err) {
      console.warn('[Optimizely] Failed to fetch datafile:', err)
    }

    const dataReady = (userEventData) => {
      if (!isClient()) return
      const auth = useAuthStore()
      window.userLoggedIn = auth?.loggedIn || false
      window.dispatchEvent(new Event('vfUserDataAvailable'))
      if (userEventData) {
        console.info('vfUserDataAvailable event fired')
      }
    }

    const getAttributeKeysForFlag = (flagKey: string): string[] => {
      if (!client || !datafile) return []
      if (attributesUsedByFlags.has(flagKey)) {
        return [...attributesUsedByFlags.get(flagKey)!]
      }

      const attributeKeys = resolveAttributeKeysUsedByFlag(datafile, flagKey)
      attributesUsedByFlags.set(flagKey, new Set(attributeKeys))
      return attributeKeys
    }

    const isAttributeUsedByFlag = (flagKey: string, attributeKey: string): boolean => {
      log.info(`Optimizely: Checking if attribute "${attributeKey}" is used by flag "${flagKey}"`)
      return getAttributeKeysForFlag(flagKey).includes(attributeKey)
    }

    const hasFeatureFlag = (flagKey: string): boolean => {
      if (!client) return false

      syncDatafile(parseOptimizelyDatafile(client.getOptimizelyConfig()?.getDatafile()))

      return resolveHasFeatureFlagInDatafile(datafile, flagKey)
    }

    const isDecisionFromRunningExperiment = (decision: OptimizelyDecision | undefined): boolean => {
      if (!client) return false

      syncDatafile(parseOptimizelyDatafile(client.getOptimizelyConfig()?.getDatafile()))

      return resolveRunningExperimentDecision(datafile, decision)
    }

    return {
      provide: {
        optimizely: {
          client,
          dataReady,
          hasFeatureFlag,
          getAttributeKeysUsedByFlag: getAttributeKeysForFlag,
          isAttributeUsedByFlag,
          isDecisionFromRunningExperiment
        }
      }
    }
  }
})
